import { ISalesReps } from '@llws/lift-entity-interfaces';

/**
 * A sales rep whose `user` relation is loaded, and so has a name to render.
 *
 * The `user` relation on `ISalesReps` is optional because it is only populated when a
 * query eagerly joins it, so a rep without it cannot be displayed by name.
 */
type TNamedSalesRep = ISalesReps & Required<Pick<ISalesReps, 'user'>>;

/**
 * Narrows to reps whose `user` relation is loaded.
 *
 * Reps without one render as a `MenuItem` carrying a value but no visible text — a
 * selectable blank row — so they are excluded from dropdowns rather than displayed.
 */
const hasLoadedUser = (salesRep: ISalesReps): salesRep is TNamedSalesRep => !!salesRep.user;

/**
 * Compares two name parts alphabetically, case- and accent-insensitively, treating a
 * missing name as an empty string.
 */
const compareNameParts = (
  nameA: string | null | undefined,
  nameB: string | null | undefined,
): number => (
  (nameA ?? '').localeCompare(nameB ?? '', undefined, { sensitivity: 'base' })
);

/**
 * Orders sales reps by first name then last name, matching the `First Last` label the
 * dropdowns render so a visible list reads alphabetically top to bottom.
 *
 * Exported for callers that need the comparator itself; most should use
 * {@link sortSalesRepsByName}.
 */
export const compareSalesRepsByName = (repA: ISalesReps, repB: ISalesReps): number => {
  const firstNameComparison = compareNameParts(repA.user?.firstName, repB.user?.firstName);

  return firstNameComparison !== 0
    ? firstNameComparison
    : compareNameParts(repA.user?.lastName, repB.user?.lastName);
};

/**
 * Builds the display list for a sales rep dropdown: reps with a loaded `user` relation,
 * ordered by first name then last name.
 *
 * Note this both filters and sorts — reps with no `user` relation are dropped, since they
 * would otherwise render as a selectable blank row.
 *
 * Always returns a new array. Callers pass lists held in the redux store, and
 * `Array.prototype.sort` is in-place, so sorting the input directly would reorder shared
 * state and affect every other consumer of that list.
 *
 * @param salesReps - Reps as fetched, in arbitrary order.
 * @returns A new, sorted array containing only reps that can be rendered by name.
 */
export const sortSalesRepsByName = (salesReps: ISalesReps[]): TNamedSalesRep[] => (
  salesReps
    .filter(hasLoadedUser)
    .sort(compareSalesRepsByName)
);
